import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { AppWindow, ChevronDown, ChevronRight, Hourglass, MonitorSmartphone, Package, Plus, Search, SquareTerminal, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { catalog as catalogApi, files, leases as leaseApi, sshKeys } from '@/api/endpoints'
import { saveBlob } from '@/api/client'
import type { Lease, LeaseCreate } from '@/api/types'
import { Choice } from '@/components/Choice'
import { Err } from '@/components/Err'
import { NumberField } from '@/components/NumberField'
import { Crumb, Empty, PageHead } from '@/components/layout/Page'
import { catalogModels, flavorSpec, freeOf, gpuMemoryFloorGb, gpuMemoryFloorMb, holdsCards } from '@/domain/flavor'
import { OS_LINES, OS_OTHER, osLine, osWord } from '@/domain/image'
import { LEASE_NAME_RULE, LEASE_TAG_LIMIT_RULE, MAX_GPUS, MAX_VOLUMES, leaseNameOk, leaseNameTyped, leaseTagsOver } from '@/domain/lease'
import { floorPastQuotaSentence, memoryLeftMb } from '@/domain/quota'
import { fitsSomewhere, flavorOverRoom, mostCardsOnANode, placementGap, roomShort } from '@/domain/room'
import { splitTags } from '@/domain/team'
import { usePoll } from '@/hooks/usePoll'
import { useSession } from '@/hooks/useSession'
import { useWorldRefresh } from '@/hooks/useWorld'
import { cn } from '@/lib/utils'
import { DASH, size, sizeMb } from '@/ui/format'
import { revealInPage } from '@/ui/scroll'
import { NodeRooms } from './NodeRooms'
import { PastTags, VmTags } from './VmTags'
import { QuotaBox } from './QuotaBox'
import type { Notice } from './notice'

// VM 생성 페이지. 관리자의 VM 목록(`/vms/new`)과 사용자의 내 VM(`/leases/new`)이 같은 페이지를 연다 —
// 역할이 가르는 것은 돌아갈 목록 하나다. 고르는 것은 카탈로그가 주는 것뿐이다.
//
// 절은 위에서 아래로 이름 및 태그 → 애플리케이션 및 OS 이미지 → VM 유형 → GPU → 스토리지 구성 → 노드 자원이고, 첫 절 밖의 절은 접힌다.
// **노드 자원은 보는 절이다**(`./NodeRooms.tsx`) — 고르는 칸이 없고 재는 칸도 아니라 요약에 서지 않는다. 읽지 못하면 그 절만 오류다.
// **노드를 고를 수 있는 사람은 노드부터 고른다**(B-45) — 최고 관리자와, 직위가 `노드 선택` 을 준 사람이다(F-126, 카탈로그의 `node_choice`).
// 그 사람에게는 맨 위(이름 및 태그 앞)에 `노드` 절이 서고 맨 아래의 보기 절은 없다(같은 카드를 고르는 모양으로 보인다).
// 맨 앞 `자동` 은 배치가 정한다는 것이고 본문에 싣지 않는다. 노드 카드는 CPU·메모리·디스크 남음과 GPU 모델별 남음·한 장 메모리를 보인다.
// 지금 고른 사양을 받지 못하는 노드는 잠긴 채 사유를 달고(`domain/room.ts` 의 `roomShort` — 디스크는 재지 않는다, 배치가 정한다),
// 노드를 고르면 GPU 모델과 장 수는 그 노드의 것으로 좁혀지고 그 노드의 남음을 넘는 VM 유형은 `노드 자원 부족` 으로 잠긴다.
// 고른 노드가 나중에 맞지 않게 되면 그 절이 막힘이 되고 누른 제출이 그리로 돌아온다. 고른 노드는 요약에 선다.
// **노드를 고르지 않은 신청(자동 배치)도 같은 남음으로 잰다**(B-47) — 고른 사양을 한꺼번에 받을 노드가 하나도 없으면
// 무엇을 줄이면 되는지(`domain/room.ts` 의 `placementGap`)가 그 칸의 막힘이 되고, 어느 노드도 받지 못하는 유형은 `노드 자원 부족` 으로 잠기며,
// 장 수는 한 노드에 남은 가장 많은 수까지만 선다. 디스크와, 본 뒤 다른 신청이 먼저 가져가는 경주는 서버의 409 와 알림이 말한다.
// 요약은 넓은 화면에서 오른쪽에 붙어 따라오고(sticky — 앱 스크롤 영역이 스크롤 컨테이너다), 좁으면 절들 아래에 선다.
// 요약의 라벨은 그 절로 간다. 맨 아래가 `취소`·`VM 생성` 이다.
// **고르는 칸은 모두 `Choice` 다**(F-110) — 브라우저의 select 가 아니라 사이트 모양의 목록이고, 잠긴 사양은 흐리게 서서 사유를 단다.
//
// **제출 버튼은 잠그지 않는다** — 누르면 재고, 틀린 칸마다 그 아래에 문장을 세우고 첫 칸으로 가서 포커스를 준다.
// 잠긴 버튼은 무엇을 고쳐야 하는지 말하지 않는다. 치는 동안 말하는 것(이름의 규칙·태그 수·카드의 메모리)은 그 자리에서 말하고,
// 비어 있는 칸은 한 번 누른 뒤부터 말한다. 서버의 거절은 버튼 위의 `Err` 다.
// **만드는 것은 그 버튼 하나다** — 칸에서 친 Enter 는 보내지 않는다. 검색어를 치고 누른 Enter 가 찾던 이미지가 아니라
// 지금 골라진 것으로 VM 을 만들고, 태그 칸의 Enter 는 칩을 세우려던 것이다.
//
// **GPU 는 사양과 따로 고른다**: 모델과 장 수이고 어느 카드인지는 배치가 고른다. 고른 카드의 메모리 하한을 채우지 못하는
// VM 유형은 잠긴 채 서고 라벨이 그 이유를 말한다 — 메모리를 자유로 둔 유형은 그 칸이 하한을 잰다.
// 그 전에 만든 유형이 카드를 말하면 그 유형을 고를 때 그 카드가 기본값으로 서고, 손대지 않으면 본문에 싣지 않는다 —
// 서버가 유형의 카드를 쓴다. 그 유형에는 `없음` 이 없다(카드를 고르지 않은 신청은 유형의 카드를 받는다).
// **요청 사유는 승인을 기다리는 신청(관리자가 아닌 사람의 카드가 있는 신청)에만 선다** — 승인하는 사람이 읽는 것이라 그 신청에는 필수다.
//
// 성공하면 생성된 접속 키 링크를 여기서 즉시 한 번 받아 저장하고 버린다 — state 에도 화면에도 남지 않는다.
// 결과 문장은 경로의 state 로 목록에 넘기고(`./notice.ts`), 관리자의 목록은 곧바로 다시 읽는다 —
// 변경 스트림은 최고 관리자만 받아 관리자는 다음 폴링까지 방금 만든 VM 을 보지 못한다.
// 보내는 동안 `취소` 는 눌리지 않는다 — 이미 보낸 요청은 거둘 수 없어 취소가 VM 을 막지 못한다.
// 그래도 다른 화면으로 떠났으면 끝난 요청이 사람을 데려오지 않고 키도 받지 않는다: react-router 의 nav 는
// 떠난 페이지에서도 움직여 다른 화면에 있던 사람을 목록으로 끌고 온다. 키는 VM 상세의 `키 받기` 가 다시 준다.

const CATALOG_POLL_MS = 60000
const NODES_POLL_MS = 30000
const NODES_ID = 'vm-part-nodes'
const COUNTS = Array.from({ length: MAX_GPUS }, (_, i) => i + 1)

// 절과 그 앵커. 요약의 라벨과 첫 오류 칸이 이 id 로 간다.
type Part = 'node' | 'name' | 'image' | 'type' | 'gpu' | 'storage'
const PART_ID: Record<Part, string> = {
  node: 'vm-part-node',
  name: 'vm-part-name',
  image: 'vm-part-image',
  type: 'vm-part-type',
  gpu: 'vm-part-gpu',
  storage: 'vm-part-storage',
}

// 재는 칸. 순서가 페이지의 순서다 — 첫 오류 칸이 이 순서로 정해진다.
type Field = 'node' | 'name' | 'tags' | 'image' | 'flavor' | 'cores' | 'memory' | 'gpu' | 'reason'
const FIELDS: readonly { field: Field; id: string; part: Part }[] = [
  { field: 'node', id: 'vm-node', part: 'node' },
  { field: 'name', id: 'vm-name', part: 'name' },
  { field: 'tags', id: 'vm-tags', part: 'name' },
  { field: 'image', id: 'vm-image', part: 'image' },
  { field: 'flavor', id: 'vm-flavor', part: 'type' },
  { field: 'cores', id: 'vm-cores', part: 'type' },
  { field: 'memory', id: 'vm-memory', part: 'type' },
  { field: 'gpu', id: 'vm-gpus', part: 'gpu' },
  { field: 'reason', id: 'vm-reason', part: 'gpu' },
]

// OS 의 아이콘. 상표가 아니라 종류다 — 창이 있는 OS, 터미널의 OS, 그 밖.
function OsIcon({ word, className }: { word: string; className: string }) {
  if (word === 'Windows') return <AppWindow className={className} aria-hidden />
  if (word === OS_OTHER) return <Package className={className} aria-hidden />
  return <SquareTerminal className={className} aria-hidden />
}

const hint = (bad: boolean) => (bad ? 'text-[11px] text-danger-ink' : 'text-[11px] text-muted-foreground')

export default function CreateVm() {
  const me = useSession()
  const nav = useNavigate()
  const refresh = useWorldRefresh()
  // 돌아갈 목록 — 사용자의 내 VM 과 관리자의 VM 목록.
  const list = me.role === 'user' ? { to: '/', word: '내 VM' } : { to: '/vms', word: 'VM' }
  // 열 때 카탈로그를 새로 읽는다 — 유형 목록과 내 쿼터는 그 순간의 값이어야 한다.
  const catalog = usePoll(() => catalogApi.get(), CATALOG_POLL_MS)
  const cat = catalog.data
  const rooms = usePoll(() => catalogApi.nodes(), NODES_POLL_MS)
  const [nodesOpen, setNodesOpen] = useState(true)
  // 고른 노드의 id. 고를 수 있는 사람에게만 서버가 노드의 id 를 싣는다 — ''가 `자동` 이다.
  const [nodeSel, setNodeSel] = useState('')
  // 자기 기기 키를 등록한 사람은 VM 이름만으로 들어가므로 생성 키를 내려받지 않는다(#628) — 상세의 `키 받기` 는 그대로다.
  // 목록을 읽지 못했으면 지금처럼 받는다: 키 없이 남는 것보다 파일이 하나 더 있는 편이 낫다.
  const keys = usePoll(() => sshKeys.list(), CATALOG_POLL_MS)
  const hasOwnKey = keys.data?.items.some((k) => !k.lease_id) ?? false

  const [name, setName] = useState('')
  const [tagsOpen, setTagsOpen] = useState(false)
  const [tags, setTags] = useState('')
  const [find, setFind] = useState('')
  const [imageSel, setImageSel] = useState('')
  const [flavorSel, setFlavorSel] = useState('')
  // 유형이 자유로 둔 자원만 이 칸들이 채운다.
  const [coresSel, setCoresSel] = useState('')
  const [memSel, setMemSel] = useState('')
  // 사람이 고른 카드. 빈 모델은 고르지 않은 것이고 0 장은 장 수를 고르지 않은 것이다.
  // 장 수는 모델을 고른 동안만 사람의 값이다 — `없음` 이 거두고, 그 전에 만든 유형의 카드에는 쓰지 않는다.
  const [gpuSel, setGpuSel] = useState('')
  const [countSel, setCountSel] = useState(0)
  const [reason, setReason] = useState('')
  const [diskSel, setDiskSel] = useState(0)
  // 루트 밖에 더한 볼륨의 크기(GB), 더한 순서대로.
  const [volumes, setVolumes] = useState<number[]>([])
  const [open, setOpen] = useState<Record<Exclude<Part, 'name'>, boolean>>({ node: true, image: true, type: true, gpu: true, storage: true })
  const [summaryOpen, setSummaryOpen] = useState(true)
  // 한 번 누른 뒤부터 빈 칸이 막힘을 말한다.
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  // 누를 때마다 첫 오류 칸으로 간다 — 같은 칸이 두 번 틀려도 다시 간다.
  const [attempt, setAttempt] = useState(0)
  const focusRef = useRef<{ id: string; part: Part } | null>(null)
  // 페이지가 아직 서 있는가 — 보낸 뒤 떠났으면 끝난 요청이 사람을 데려오지 않는다.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    const target = focusRef.current
    if (!target) return
    focusRef.current = null
    const el = document.getElementById(target.id) ?? document.getElementById(PART_ID[target.part])
    // 셸까지 밀어 올리지 않게 앱의 스크롤 영역만 움직인다(#649).
    if (el) revealInPage(el, 'center')
    el?.focus({ preventScroll: true })
  }, [attempt])

  // ── 고른 것 ─────────────────────────────────────────────────────────────
  const images = cat?.images ?? []
  const image = imageSel || images[0]?.id || ''
  const chosenImage = images.find((i) => i.id === image)
  const family = chosenImage ? osWord(chosenImage.os_family) : ''
  const line = chosenImage ? osLine(chosenImage.os_family) : ''
  // 검색은 이미지 이름과 OS 의 낱말과 계열을 본다. 고른 이미지는 걸러져도 select 에 남는다 — 값이 사라지지 않게.
  const needle = find.trim().toLowerCase()
  const hit = (i: { name: string; os_family?: string | null }) =>
    needle === '' ||
    i.name.toLowerCase().includes(needle) ||
    osWord(i.os_family).toLowerCase().includes(needle) ||
    osLine(i.os_family).toLowerCase().includes(needle)
  const families = [...new Set(images.filter(hit).map((i) => osWord(i.os_family)))]
  // 타일은 계열마다 묶인다 — Debian 계열, Red Hat 계열, 그 밖.
  const lineOfWord = (word: string) => osLine(images.find((i) => osWord(i.os_family) === word)?.os_family)
  const lines = OS_LINES.map((l) => [l, families.filter((w) => lineOfWord(w) === l)] as const).filter(([, ws]) => ws.length > 0)
  const familyImages = images.filter((i) => osWord(i.os_family) === family && (hit(i) || i.id === image))

  // 노드를 고르는 사람이 고른 노드. 고를 수 없는 사람에게는 늘 없다 — 보이지 않는 칸의 값이 본문에 실리지 않게.
  const chooser = cat?.node_choice === true
  const picked = chooser && nodeSel !== '' ? (rooms.data ?? []).find((r) => r.id === nodeSel) : undefined
  // 한 장의 메모리는 `/catalog` 의 모델 항목이 말한다 — 승인 창과 같은 목록이다.
  const vramOf = (model: string) => catalogModels(cat?.gpus ?? []).find((m) => m.model === model)?.vramMb ?? 0
  // 고른 카드의 하한(MB). 이 하한을 채우지 못하는 유형은 잠기고, 고르지 않은 유형의 첫 값도 이 하한을 채우는 유형이다.
  const pickedFloorMb = gpuSel ? gpuMemoryFloorMb(vramOf(gpuSel), countSel || 1) : 0
  const enabled = cat?.flavors.filter((f) => f.enabled) ?? []
  // 고르지 않았으면 카탈로그의 첫 값이다 — 상태를 카탈로그에 맞춰 다시 쓰지 않고 읽을 때 정한다.
  // 노드를 고르지 않았으면(자동 배치, B-47) 어느 노드라도 그 유형과 고른 카드를 함께 받을 수 있어야 한다 — 자유로 둔 축은 칸이 잰다.
  const fitsNode = (f: (typeof enabled)[number]) =>
    picked
      ? !flavorOverRoom(f, picked)
      : fitsSomewhere(rooms.data ?? [], { cores: f.cores ?? 0, memoryMb: f.memory_mb ?? 0, gpuModel: gpuSel, gpus: gpuSel ? countSel || 1 : 0 })
  const flavor = flavorSel || (enabled.find((f) => holdsCards(f, pickedFloorMb) && fitsNode(f)) ?? enabled.find((f) => holdsCards(f, pickedFloorMb)) ?? enabled[0] ?? cat?.flavors[0])?.id || ''
  const chosen = cat?.flavors.find((f) => f.id === flavor)
  const free = freeOf(chosen)
  const steps = cat?.disk_steps ?? []
  const sizes = steps.map((d) => ({ value: String(d), title: size(d) }))
  const disk = free.disk ? diskSel || steps[0] || 0 : (chosen?.disk_gb ?? 0)
  const cores = free.cores ? Number(coresSel) : (chosen?.cores ?? 0)
  const memory = free.memory ? Number(memSel) * 1024 : (chosen?.memory_mb ?? 0)
  // 그 전에 만든 유형의 카드는 사람이 고르지 않을 때의 값이다.
  const sized = chosen?.gpus != null && chosen.gpu_model != null ? { model: chosen.gpu_model, gpus: chosen.gpus } : null
  const gpuModel = gpuSel || sized?.model || ''
  // 보이는 장 수가 가는 장 수다 — 사람이 고른 모델에만 고른 장 수가 실리고, 나머지는 유형의 장 수다.
  const gpus = gpuSel ? countSel || 1 : (sized?.gpus ?? 0)
  const gpuOptions = catalogModels(cat?.gpus ?? [], sized?.model)
  // 받는 카드의 하한. 견주는 것은 백엔드처럼 MB 다 — GB 는 칸과 문장이 말하는 단위일 뿐이다.
  const floorMb = gpus > 0 ? gpuMemoryFloorMb(vramOf(gpuModel), gpus) : 0
  const floorGb = gpus > 0 ? gpuMemoryFloorGb(vramOf(gpuModel), gpus) : 0
  const flavorShort = chosen != null && !holdsCards(chosen, floorMb)
  const memoryShort = free.memory && memSel !== '' && memory < floorMb
  const gpuSize = gpus > 0
  // 카드가 있는 신청은 승인을 기다린다 — 승인하는 사람(관리자 이상)의 신청은 빼고(B-42).
  const waits = gpuSize && me.role === 'user'
  // 카드의 하한이 내 메모리 쿼터에 남은 것보다 크면 알린다(F-101). 막지 않는다 — 카드를 실은 신청은
  // 메모리 쿼터로 거절되지 않고, 승인하는 사람이 메모리를 정한다.
  const left = cat?.quota ? memoryLeftMb(cat.quota) : null
  const floorPastQuota = waits && left !== null && floorMb > left
  const tagList = splitTags(tags)
  // 노드가 받아야 하는 것 — 지금 고른 사양. 노드 카드의 잠김과 고른 노드의 막힘이 같은 산수다.
  const need = { cores, memoryMb: memory, gpuModel, gpus }
  // 고른 노드에 있는 그 모델의 카드. 노드를 고르면 모델과 장 수는 그 노드의 것으로 좁혀진다.
  const onNode = (model: string) => picked?.gpus.find((g) => g.model === model)
  // 자동 배치에서도 한 VM 은 한 노드에 놓이므로 장 수의 끝은 한 노드에 남은 가장 많은 수다(B-47). 노드를 읽지 못했으면 재지 않는다.
  const maxCount = picked
    ? Math.min(MAX_GPUS, onNode(gpuModel)?.free ?? 0)
    : rooms.data && rooms.data.length > 0
      ? Math.min(MAX_GPUS, mostCardsOnANode(rooms.data, gpuModel))
      : MAX_GPUS
  const totalDisk = disk + volumes.reduce((a, b) => a + b, 0)

  // ── 재기 ────────────────────────────────────────────────────────────────
  const problem: Partial<Record<Field, string>> = {}
  if (name === '') problem.name = 'VM 이름을 적으세요.'
  else if (!leaseNameOk(name)) problem.name = LEASE_NAME_RULE
  if (leaseTagsOver(tags)) problem.tags = LEASE_TAG_LIMIT_RULE
  if (picked && roomShort(picked, need)) problem.node = '요청 자원을 줄이거나 다른 노드를 선택하세요.'
  // 노드를 고르지 않은 신청도 받을 노드가 없으면 보내지 않는다(B-47) — 무엇을 줄이면 되는지를 그 칸에서 말한다.
  const gap = !picked && rooms.data ? placementGap(rooms.data, need, { cores: !free.cores, memory: !free.memory }) : null
  if (!image) problem.image = '이미지를 고르세요.'
  if (!flavor) problem.flavor = 'VM 유형을 고르세요.'
  else if (flavorShort) problem.flavor = `이 GPU 에는 메모리가 ${size(floorGb)} 이상 필요합니다.`
  else if (picked && chosen && flavorOverRoom(chosen, picked)) problem.flavor = '고른 노드에 남은 자원으로는 이 유형을 받을 수 없습니다.'
  if (free.cores && !(cores > 0)) problem.cores = '코어 수를 적으세요.'
  if (free.memory && !(memory > 0)) problem.memory = '메모리를 적으세요.'
  else if (memoryShort) problem.memory = `${floorGb}GB 이상`
  if (gap && !problem[gap.field]) problem[gap.field] = gap.text
  if (waits && reason.trim() === '') problem.reason = 'GPU 요청 사유를 적으세요.'
  // 치는 동안 말하는 막힘. 나머지는 한 번 누른 뒤부터 말한다 — 아직 치지 않은 칸은 틀린 칸이 아니다.
  const live: Record<Field, boolean> = {
    node: true,
    name: name !== '',
    tags: true,
    image: false,
    flavor: flavorShort,
    cores: false,
    memory: memoryShort,
    gpu: false,
    reason: false,
  }
  // 받을 노드가 없다는 막힘은 고른 노드의 막힘처럼 그 자리에서 말한다 — 눌러 보기 전에 줄일 것을 안다.
  if (gap) live[gap.field] = true
  const said = (f: Field) => (tried || live[f] ? problem[f] : undefined)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || !cat) return
    const first = FIELDS.find((f) => problem[f.field])
    if (first) {
      setTried(true)
      if (first.part !== 'name') setOpen((o) => ({ ...o, [first.part]: true }))
      focusRef.current = { id: first.id, part: first.part }
      setAttempt((n) => n + 1)
      return
    }
    setBusy(true)
    setError(null)
    const body: LeaseCreate = {
      name,
      flavor_id: flavor,
      image_id: image,
      // 고정된 자원은 싣지 않는다 — 유형이 이미 말한 값을 다시 말하면 두 진실이 된다.
      ...(free.cores ? { cores } : {}),
      ...(free.memory ? { memory_mb: memory } : {}),
      ...(free.disk ? { disk_gb: disk } : {}),
      // 사람이 고른 카드만 싣는다. 손대지 않은 유형의 카드는 서버가 유형에서 읽는다.
      ...(gpuSel ? { gpu_model: gpuSel, gpus } : {}),
      tags: tagList,
      ...(waits ? { purpose: reason.trim() } : {}),
      ...(volumes.length > 0 ? { volumes: volumes.map((size_gb) => ({ size_gb })) } : {}),
      ...(picked?.id ? { node_id: picked.id } : {}),
    }
    let l: Lease
    try {
      l = await leaseApi.create(body)
    } catch (err) {
      setError(err)
      setBusy(false)
      return
    }
    // 여기부터는 VM 이 이미 맡겨진 뒤다 — 무엇이 실패해도 생성의 실패로 말하지 않는다(다시 누르면 같은 VM 을 또 만든다).
    refresh()
    // 그 사이 떠났으면 데려오지도 키를 받지도 않는다 — 다른 화면에서 까닭 없는 파일이 내려온다.
    if (!alive.current) return
    // 링크는 응답 객체에만 있고 여기서 소비된다. 이 함수를 벗어나는 것은 한 줄 안내뿐이다.
    const completed = l.state === 'pending_approval' ? '승인 요청이 완료됐습니다.' : '생성을 요청했습니다.'
    let notice: Notice = { tone: 'ok', text: completed }
    if (l.ssh_private_key_download_url && !hasOwnKey) {
      try {
        const d = await files.downloadOnce(l.ssh_private_key_download_url)
        saveBlob(d, l.ssh_key_file ?? `${l.owner.username}-${l.name}.key`)
        notice = { tone: 'ok', text: `${completed} 내려받은 키 파일을 보관하세요.` }
      } catch {
        notice = { tone: 'danger', text: `${completed} 키 파일은 상세 화면에서 받으세요.` }
      }
    }
    if (alive.current) void nav(list.to, { state: { notice } })
  }
  // 칸의 Enter 는 보내지 않는다 — 만드는 것은 `VM 생성` 버튼 하나다. 여러 줄 칸(요청 사유)의 Enter 는 줄바꿈이라 그대로다.
  const noEnter = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) e.preventDefault()
  }

  // 요약의 라벨은 그 절로 간다 — 접혀 있으면 펴고 간다.
  const jump = (part: Part) => {
    if (part !== 'name') setOpen((o) => ({ ...o, [part]: true }))
    const el = document.getElementById(PART_ID[part])
    if (el) revealInPage(el, 'start', true)
  }
  const toggle = (part: Exclude<Part, 'name'>) => (v: boolean) => setOpen((o) => ({ ...o, [part]: v }))
  const pickFamily = (word: string) => {
    if (word === family) return
    const first = images.find((i) => osWord(i.os_family) === word && hit(i))
    if (first) setImageSel(first.id)
  }
  const setVolume = (i: number, gb: number) => setVolumes((v) => v.map((x, j) => (j === i ? gb : x)))

  const crumb = <Crumb steps={[{ label: list.word, to: list.to }, { label: 'VM 생성' }]} />
  const head = <PageHead icon={<MonitorSmartphone className="size-5" />} title="VM 생성" />
  if (!cat) {
    return (
      <>
        {crumb}
        {head}
        <div className="mt-5">
          {catalog.error ? <Err error={catalog.error} /> : <Empty>불러오는 중</Empty>}
        </div>
      </>
    )
  }

  const nodeErr = said('node')
  const nameErr = said('name')
  const tagsErr = said('tags')
  const flavorErr = said('flavor')
  const coresErr = said('cores')
  const memErr = said('memory')
  const gpuErr = said('gpu')
  const reasonErr = said('reason')

  return (
    <>
      {crumb}
      {head}
      <Err error={catalog.error} />
      {/* 넓은 화면에서는 두 열이고 요약이 오른쪽에 붙어 따라온다. 좁으면 한 열이고 요약이 맨 아래다.
          두 열은 2 대 1 이다 — 요약을 고정 폭으로 두면 사이드바가 펴진 lg 의 경계에서 절의 열이 요약만큼 좁아진다. */}
      <form
        onSubmit={submit}
        onKeyDown={noEnter}
        noValidate
        className="mt-5 grid items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(15rem,1fr)]"
      >
        <div className="grid min-w-0 gap-4">
          {chooser ? (
            <Card id={PART_ID.node} title="노드" open={open.node} onOpen={toggle('node')}>
              {rooms.data ? (
                <NodeRooms
                  id="vm-node"
                  rooms={rooms.data}
                  vramMb={vramOf}
                  pick={{ value: picked?.id ?? '', onChange: setNodeSel, locked: (r) => roomShort(r, need) }}
                />
              ) : rooms.error ? null : (
                <Empty>불러오는 중</Empty>
              )}
              {nodeErr ? <span className={hint(true)}>{nodeErr}</span> : null}
              <Err error={rooms.error} />
            </Card>
          ) : null}
          <Card id={PART_ID.name} title="이름 및 태그">
            <div className="grid gap-1.5">
              <Label htmlFor="vm-name">이름</Label>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <Input
                  id="vm-name"
                  value={name}
                  onChange={(e) => setName(leaseNameTyped(e.target.value))}
                  placeholder="예: my-vm"
                  maxLength={32}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-invalid={nameErr ? true : undefined}
                  aria-describedby="vm-name-rule"
                  className="min-w-0 flex-1 basis-56 text-base sm:text-[13px]"
                />
                {tagsOpen ? null : (
                  <button type="button" onClick={() => setTagsOpen(true)} className="text-[13px] font-medium text-brand hover:underline">
                    태그 추가
                  </button>
                )}
              </div>
              {/* 규칙은 치는 동안 그 자리에서 말한다 — 어기면 같은 줄이 막힘이 된다. */}
              <span id="vm-name-rule" className={hint(Boolean(nameErr))}>
                {nameErr ?? LEASE_NAME_RULE}
              </span>
            </div>
            {tagsOpen ? (
              <div className="grid gap-1.5">
                <Label htmlFor="vm-tags">태그</Label>
                <Input
                  id="vm-tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="예: 수업, 연구실"
                  autoComplete="off"
                  autoFocus
                  aria-invalid={tagsErr ? true : undefined}
                  className="text-base sm:text-[13px]"
                />
                {tagsErr ? <span className={hint(true)}>{tagsErr}</span> : null}
                <VmTags tags={tagList} />
                <PastTags raw={tags} onChange={setTags} />
              </div>
            ) : null}
          </Card>

          <Card id={PART_ID.image} title="애플리케이션 및 OS 이미지" open={open.image} onOpen={toggle('image')}>
            {images.length === 0 ? (
              <Empty>이미지 없음</Empty>
            ) : (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={find}
                    onChange={(e) => setFind(e.target.value)}
                    aria-label="이미지 검색"
                    className="pl-9 text-base sm:text-[13px]"
                  />
                </div>
                <div className="border-b">
                  <span className="-mb-px inline-block border-b-2 border-brand px-3 pb-2 text-[13px] font-semibold text-brand">빠른 시작</span>
                </div>
                {families.length === 0 ? (
                  <Empty>검색 결과 없음</Empty>
                ) : (
                  <div role="group" aria-label="OS" className="grid gap-3">
                    {lines.map(([l, words]) => (
                      <div key={l} role="group" aria-label={l} className="grid gap-1.5">
                        <span className="text-[12px] font-semibold text-muted-foreground">{l}</span>
                        <div className="flex flex-wrap gap-2">
                          {words.map((word) => {
                            const on = word === family
                            return (
                              <button
                                key={word}
                                type="button"
                                aria-pressed={on}
                                onClick={() => pickFamily(word)}
                                className={cn(
                                  'flex size-24 flex-col items-center justify-between rounded-lg border bg-card px-2 py-3 text-center text-[12px] font-medium hover:bg-surface-2',
                                  on ? 'border-brand bg-brand/5 ring-1 ring-brand' : 'border-border',
                                )}
                              >
                                <span className="break-words">{word}</span>
                                <OsIcon word={word} className={cn('size-7', on ? 'text-brand' : 'text-muted-foreground')} />
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid gap-1.5">
                  <Label htmlFor="vm-image">이미지</Label>
                  <Choice
                    id="vm-image"
                    value={image}
                    icon={<OsIcon word={family} className="size-4 flex-none text-muted-foreground" />}
                    onChange={setImageSel}
                    items={familyImages.map((i) => ({ value: i.id, title: i.name, detail: osWord(i.os_family) }))}
                  />
                </div>
                <dl className="flex flex-wrap gap-x-8 gap-y-2">
                  {(
                    [
                      ['OS 계열', line || DASH],
                      ['OS 종류', family || DASH],
                      ['이미지', chosenImage?.name ?? DASH],
                      ['사용자 이름', me.username],
                    ] as const
                  ).map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <dt className="text-[12px] font-semibold">{k}</dt>
                      <dd className="break-words text-muted-foreground">{v}</dd>
                    </div>
                  ))}
                </dl>
              </>
            )}
          </Card>

          <Card id={PART_ID.type} title="VM 유형" open={open.type} onOpen={toggle('type')}>
            {enabled.length === 0 ? (
              <Empty>VM 유형 없음</Empty>
            ) : (
              <div className="grid gap-1.5">
                <Label htmlFor="vm-flavor">VM 유형</Label>
                <Choice
                  id="vm-flavor"
                  value={flavor}
                  invalid={Boolean(flavorErr)}
                  onChange={setFlavorSel}
                  items={enabled.map((f) => {
                    // 고른 카드를 받칠 메모리가 없는 유형과 고른 노드에 남은 자원을 넘는 유형은 고를 수 없다 — 왜인지는 사유가 말한다.
                    const short = !holdsCards(f, pickedFloorMb)
                    const over = !fitsNode(f)
                    return {
                      value: f.id,
                      title: f.display_name,
                      detail: flavorSpec(f),
                      disabled: short || over,
                      reason: short ? '메모리 부족' : over ? '노드 자원 부족' : undefined,
                    }
                  })}
                />
                {flavorErr ? (
                  <span className={hint(true)}>{flavorErr}</span>
                ) : floorMb > 0 && !free.memory ? (
                  <span className={hint(false)}>메모리 {size(floorGb)} 이상</span>
                ) : null}
              </div>
            )}
            {free.cores || free.memory ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {free.cores ? (
                  <NumberField id="vm-cores" label="코어" value={coresSel} placeholder="" busy={busy} onChange={setCoresSel} hint={coresErr ?? ''} bad={Boolean(coresErr)} />
                ) : null}
                {free.memory ? (
                  // 카드가 있으면 그 하한을 칸 아래가 말하고, 그 아래를 적으면 같은 자리가 막힘이 된다.
                  // placeholder 는 비운 칸이 무엇이 되는지를 말하는 자리인데 여기서 비운 칸은 막힘이라 비워 둔다.
                  <NumberField
                    id="vm-memory"
                    label="메모리 (GB)"
                    value={memSel}
                    placeholder=""
                    busy={busy}
                    onChange={setMemSel}
                    hint={memErr ?? (floorGb > 0 ? `${floorGb}GB 이상` : '')}
                    bad={Boolean(memErr)}
                  />
                ) : null}
              </div>
            ) : null}
          </Card>

          {/* 고를 모델이 없으면 절이 서지 않는다 — `없음` 하나뿐인 select 는 고르는 자리가 아니다. */}
          {gpuOptions.length > 0 ? (
            <Card id={PART_ID.gpu} title="GPU" open={open.gpu} onOpen={toggle('gpu')}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="vm-gpu">모델</Label>
                  <Choice
                    id="vm-gpu"
                    value={gpuModel}
                    onChange={(v) => {
                      setGpuSel(v)
                      if (v === '') setCountSel(0)
                    }}
                    items={[
                      ...(sized ? [] : [{ value: '', title: '없음' }]),
                      ...gpuOptions.map((m) => {
                        if (!picked) return { value: m.model, title: m.model, detail: m.label.startsWith(`${m.model} · `) ? m.label.slice(m.model.length + 3) : undefined }
                        // 노드를 골랐으면 남은 장 수는 그 노드의 것이고, 그 노드에서 받을 수 없는 모델은 잠긴다.
                        const g = onNode(m.model)
                        const locked = (!g || g.free === 0) && m.model !== gpuModel
                        return {
                          value: m.model,
                          title: m.model,
                          detail: g ? `최대 ${g.free}장` : undefined,
                          disabled: locked,
                          reason: !g ? '이 노드에 없음' : g.free === 0 ? '남은 카드 없음' : undefined,
                        }
                      }),
                    ]}
                  />
                </div>
                {gpuSize ? (
                  <div className="grid gap-1.5">
                    <Label htmlFor="vm-gpus">장 수</Label>
                    {/* 장 수를 고치면 유형의 카드도 사람이 고른 카드가 된다 — 본문에 실어야 그 장 수가 간다. */}
                    <Choice
                      id="vm-gpus"
                      compact
                      value={String(gpus)}
                      invalid={Boolean(gpuErr)}
                      onChange={(v) => {
                        setGpuSel(gpuModel)
                        setCountSel(Number(v))
                      }}
                      items={COUNTS.filter((n) => n <= Math.max(maxCount, 1) || n === gpus).map((n) => ({ value: String(n), title: `${n}장` }))}
                    />
                    {gpuErr ? <span className={hint(true)}>{gpuErr}</span> : null}
                  </div>
                ) : null}
              </div>
              {waits ? (
                <>
                  <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <Hourglass className="size-3.5" aria-hidden />
                    승인 뒤 생성
                  </span>
                  {floorPastQuota ? <p className="text-[12px] text-warn-ink">{floorPastQuotaSentence(floorMb)}</p> : null}
                  <div className="grid gap-1.5">
                    <Label htmlFor="vm-reason">요청 사유</Label>
                    <Textarea
                      id="vm-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      maxLength={200}
                      rows={3}
                      aria-invalid={reasonErr ? true : undefined}
                      className="text-base sm:text-[13px]"
                    />
                    {reasonErr ? <span className={hint(true)}>{reasonErr}</span> : null}
                  </div>
                </>
              ) : null}
            </Card>
          ) : null}

          <Card id={PART_ID.storage} title="스토리지 구성" open={open.storage} onOpen={toggle('storage')}>
            {/* 한 줄 — 이름 · 크기 · 빼기. 줄어드는 것은 크기 칸 하나다. */}
            <div className="grid gap-2.5">
              <div className={DISK_ROW}>
                {free.disk ? (
                  <>
                    <Label htmlFor="vm-disk">루트 디스크</Label>
                    <Choice id="vm-disk" compact value={String(disk)} onChange={(v) => setDiskSel(Number(v))} items={sizes} />
                  </>
                ) : (
                  <>
                    <span className="text-sm leading-none font-medium">루트 디스크</span>
                    <span>{disk > 0 ? size(disk) : DASH}</span>
                  </>
                )}
              </div>
              {volumes.map((gb, i) => (
                <div key={i} className={DISK_ROW}>
                  <Label htmlFor={`vm-volume-${i}`}>데이터 볼륨 {i + 1}</Label>
                  <Choice id={`vm-volume-${i}`} compact value={String(gb)} onChange={(v) => setVolume(i, Number(v))} items={sizes} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`데이터 볼륨 ${i + 1} 빼기`}
                    onClick={() => setVolumes((v) => v.filter((_, j) => j !== i))}
                  >
                    <X />
                  </Button>
                </div>
              ))}
              <div>
                {/* 상한에 닿으면 잠길 뿐 문장은 없다. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={volumes.length >= MAX_VOLUMES || steps.length === 0}
                  onClick={() => setVolumes((v) => [...v, steps[0]!])}
                >
                  <Plus className="size-3.5" />새 볼륨 추가
                </Button>
              </div>
            </div>
          </Card>

          {chooser ? null : (
            <Card id={NODES_ID} title="노드 자원" open={nodesOpen} onOpen={setNodesOpen}>
              {rooms.data ? <NodeRooms rooms={rooms.data} vramMb={vramOf} /> : rooms.error ? null : <Empty>불러오는 중</Empty>}
              <Err error={rooms.error} />
            </Card>
          )}
        </div>

        <aside aria-labelledby="vm-summary" className="min-w-0 rounded-xl border bg-card p-5 max-sm:p-4 lg:sticky lg:top-4">
          <Collapsible open={summaryOpen} onOpenChange={setSummaryOpen}>
            <h2 id="vm-summary" className="text-[15px] font-semibold">
              <CollapsibleTrigger className="flex items-center gap-1.5 text-left">
                {summaryOpen ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
                요약
              </CollapsibleTrigger>
            </h2>
            <CollapsibleContent className="mt-4 grid gap-4">
              <dl className="grid gap-3">
                <Entry label="이미지" onJump={() => jump('image')}>
                  {chosenImage?.name ?? DASH}
                </Entry>
                <Entry label="VM 유형" onJump={() => jump('type')}>
                  {chosen ? (
                    <>
                      {chosen.display_name}
                      <div className="text-[12px] text-muted-foreground">
                        코어 {cores > 0 ? cores : DASH} · 메모리 {memory > 0 ? sizeMb(memory) : DASH}
                      </div>
                    </>
                  ) : (
                    DASH
                  )}
                </Entry>
                {gpuOptions.length > 0 ? (
                  <Entry label="GPU" onJump={() => jump('gpu')}>
                    {gpuSize ? `${gpuModel} ${gpus}장` : '없음'}
                  </Entry>
                ) : null}
                <Entry label="스토리지(볼륨)" onJump={() => jump('storage')}>
                  {1 + volumes.length}개의 볼륨 – 합계 {size(totalDisk)}
                </Entry>
                {picked ? (
                  <Entry label="노드" onJump={() => jump('node')}>
                    {picked.name}
                  </Entry>
                ) : null}
                <Entry
                  label="태그"
                  onJump={() => {
                    setTagsOpen(true)
                    jump('name')
                  }}
                >
                  {tagList.length > 0 ? <VmTags tags={tagList} /> : '없음'}
                </Entry>
              </dl>
              {cat.quota ? (
                <QuotaBox quota={cat.quota} add={{ vms: 1, cores, memory_mb: memory, disk_gb: totalDisk }} waived={gpuSize ? ['memory_mb'] : []} />
              ) : null}
            </CollapsibleContent>
          </Collapsible>
          <Separator className="my-4" />
          <div className="grid gap-3">
            <Err error={error} />
            <div className="flex items-center justify-between gap-3">
              {/* 보내는 동안은 눌리지 않는다 — 이미 보낸 요청은 거둘 수 없다. */}
              <Link
                to={list.to}
                aria-disabled={busy || undefined}
                onClick={(e) => {
                  if (busy) e.preventDefault()
                }}
                className="text-[13px] font-medium text-brand hover:underline aria-disabled:pointer-events-none aria-disabled:text-muted-foreground"
              >
                취소
              </Link>
              <Button type="submit" disabled={busy} className="rounded-full bg-brand px-5 hover:bg-brand-hover">
                VM 생성
              </Button>
            </div>
            {waits ? <span className="text-right text-[12px] text-muted-foreground">승인 뒤 생성</span> : null}
          </div>
        </aside>
      </form>
    </>
  )
}

// 스토리지 한 줄 — 이름 · 크기 · 빼기. 줄어드는 것은 크기 칸 하나다.
const DISK_ROW = 'grid grid-cols-[6.5rem_minmax(0,1fr)_2.25rem] items-center gap-2'

/**
 * 절 하나. 첫 절 밖의 절은 제목 왼쪽의 꺾쇠로 접힌다(`open`·`onOpen`). 접어도 고른 값은 그대로다 —
 * 값은 페이지가 갖고 칸만 내려간다.
 */
function Card({
  id,
  title,
  open,
  onOpen,
  children,
}: {
  id: string
  title: string
  open?: boolean
  onOpen?: (open: boolean) => void
  children: ReactNode
}) {
  const box = 'min-w-0 scroll-mt-4 rounded-xl border bg-card p-5 max-sm:p-4'
  if (open === undefined || onOpen === undefined) {
    return (
      <section id={id} className={box}>
        <h2 className="text-[15px] font-semibold">
          {title}
        </h2>
        <div className="mt-4 grid gap-4">{children}</div>
      </section>
    )
  }
  return (
    <Collapsible asChild open={open} onOpenChange={onOpen}>
      <section id={id} className={box}>
        <h2 className="text-[15px] font-semibold">
          <CollapsibleTrigger className="flex items-center gap-1.5 text-left">
            {open ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
            {title}
          </CollapsibleTrigger>
        </h2>
        <CollapsibleContent className="mt-4 grid gap-4">{children}</CollapsibleContent>
      </section>
    </Collapsible>
  )
}

/** 요약의 한 항목 — 라벨이 그 절로 가는 링크 모양이고 그 아래가 값이다. */
function Entry({ label, onJump, children }: { label: string; onJump: () => void; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt>
        <button type="button" onClick={onJump} className="text-[12px] font-semibold text-brand hover:underline">
          {label}
        </button>
      </dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  )
}

